import clsx from "clsx";
import type { CSSProperties } from "react";
import React, { useEffect, useId, useRef, useState } from "react";
import * as ReactDOM from "react-dom";
import { CSSTransition } from "react-transition-group";
import { useOnClickOutside, useResize } from "../../utils";
import { useKeyDown } from "../../utils/hooks/keydown";
import type { ISizable } from "../types";
import { cssUnit, getSizes } from "../types";
import styles from "./DropDown.module.scss";

export interface DropDownProps
  extends ReturnType<typeof useDropDown>,
    ISizable {
  button: JSX.Element;
  children: JSX.Element;
  className?: string;
  style?: React.CSSProperties;
  nudgeX?: number;
  nudgeY?: number;
  stayOpenOnClick?: boolean;
  ignoreComponents?: Parameters<typeof useOnClickOutside>[0];
  sameWidthAsInput?: boolean;
  disableYOverflow?: boolean;
  disableXOverflow?: boolean;
  disabled?: boolean;
}

export const useDropDown = () => {
  const [isOpen, setOpen] = useState(false);
  const open = () => setOpen(true);
  const close = () => setOpen(false);
  const toggle = () => setOpen((v) => !v);

  return {
    isOpen,
    open,
    close,
    toggle,
  };
};

const DropDown = (props: DropDownProps) => {
  const {
    className,
    button,
    children,
    close,
    isOpen,
    open,
    toggle,
    ignoreComponents,
    nudgeX = 0,
    nudgeY = 0,
    sameWidthAsInput,
    stayOpenOnClick,
    style,
    disableXOverflow,
    disableYOverflow,
    disabled,
  } = props;
  useKeyDown(close, { code: "Escape" });
  type TDimensions = ReturnType<typeof getDimensions>;
  const calcLeft = (dimensions: TDimensions) => {
    if (!dimensions.button) return 0;
    return (
      dimensions.button?.left -
      ((sameWidthAsInput ? dimensions.button?.width : dimensions.menu?.width) ||
        0) +
      dimensions.button?.width +
      nudgeX
    );
  };
  const calcTop = (dimensions: TDimensions) => {
    if (!dimensions.button) return 0;
    const { scrollTop } = document.documentElement;
    return (
      dimensions.button?.top + nudgeY + dimensions.button?.height + scrollTop
    );
  };

  const calcLeftOverflow = (left) => {
    if (disableXOverflow) return 0;
    return left < 0 ? -left + 16 : 0;
  };
  const calcTopOverflow = (top, height) => {
    if (disableYOverflow) return 0;
    const { scrollTop } = document.documentElement;
    const offset = top + height - (scrollTop + window.innerHeight - 16);
    if (offset > 0) return -offset;
    else return 0;
  };

  const getDimensions = () => {
    const button = (
      buttonRef.current?.attributes.getNamedItem("rel")?.value == "monta-input"
        ? buttonRef.current.parentElement?.parentElement
        : buttonRef.current
    )?.getBoundingClientRect();
    const menu = menuRef.current?.getBoundingClientRect();
    return {
      button,
      menu,
    };
  };

  const [computedStyle, setComputedStyle] =
    useState<Pick<CSSProperties, "left" | "top" | "width">>();
  const updatePositionStyle = () => {
    window.requestAnimationFrame(() => {
      const dimensions = getDimensions();
      const top = calcTop(dimensions);
      const left = calcLeft(dimensions);
      const topOverflow = calcTopOverflow(top, dimensions.menu?.height || 0);
      const leftOverflow = calcLeftOverflow(left);

      setComputedStyle({
        left: cssUnit(left + leftOverflow),
        top: cssUnit(top + topOverflow),
        width:
          sameWidthAsInput && dimensions.button
            ? cssUnit(dimensions.button.width)
            : undefined,
      });
    });
  };
  useResize(updatePositionStyle);

  const buttonRef = useRef<HTMLElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isClient = typeof window !== "undefined";
  const appContainer =
    isClient &&
    (document.querySelector("#root") || document.querySelector("#__next"));

  const handleClick = () => {
    if (buttonRef.current?.hasAttribute("disabled")) return;
    if (stayOpenOnClick) open();
    else toggle();
    setTimeout(() => {
      updatePositionStyle();
    }, 0);
  };

  useEffect(() => {
    if (isOpen) updatePositionStyle();
  }, [isOpen]);

  useOnClickOutside(
    [
      menuRef,
      buttonRef,
      ...(ignoreComponents
        ? Array.isArray(ignoreComponents)
          ? ignoreComponents
          : [ignoreComponents]
        : []),
    ],
    close,
  );

  const id = useId();
  const key = `drop-down-${id}`;

  return (
    <>
      {React.cloneElement(button, {
        ...button.props,
        ref: buttonRef,
        onClick: () => {
          if (disabled) return;
          else handleClick();
        },
      })}
      {appContainer &&
        ReactDOM.createPortal(
          <CSSTransition
            in={isOpen}
            unmountOnExit
            timeout={{ enter: 0, exit: 200 }}
            classNames={{
              enterDone: styles["drop-down--enter-done"],
              exit: styles["drop-down--exit"],
            }}
            key={`transition-${key}`}
          >
            <div
              ref={menuRef}
              key={key}
              className={clsx(styles["drop-down"], className)}
              style={{
                ...style,
                ...getSizes(props),
                ...computedStyle,
              }}
            >
              {children}
            </div>
          </CSSTransition>,
          appContainer,
        )}
    </>
  );
};

export default DropDown;
